import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { fetchApiGet } from "../utils";

// Zod Schemas for Request Validation
export const ListHoursQuerySchema = z.object({
  location_ids: z.string()
    .transform((val) => val.split(',').map(id => id.trim()))
    .pipe(z.array(z.string().uuid())),
  start_date: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
    .optional(),
  end_date: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
    .optional(),
  limit: z.coerce.number().min(1).max(1000).optional().default(100),
});

export const ListRunsParamsSchema = z.object({
  locationId: z.string().uuid()
});

export const ListRunsQuerySchema = z.object({
  service_category_id: z.string().uuid().optional(),
  limit: z.coerce.number().min(1).max(500).optional().default(50),
  run_status: z.string().optional(),
});

// Response Schemas
export const LocationSchema = z.object({
  id: z.string().uuid(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  name: z.string(),
  alias: z.string().nullable(),
  link: z.string(),
  is_enabled: z.boolean(),
  is_visible: z.boolean(),
  last_fetched: z.string().datetime().nullable(),
  last_parsed: z.string().datetime().nullable(),
  last_ingest: z.string().datetime().nullable(),
  meta_data: z.record(z.any()),
  serviceCategories: z.array(z.lazy(() => ServiceCategorySchema)),
  latest_run_id: z.string().uuid().optional()
});

export const ServiceCategorySchema = z.object({
  id: z.string().uuid(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  location_id: z.string().uuid(),
  service_name: z.string(),
  service_group: z.string().nullable(),
  is_active: z.boolean(),
  display_order: z.number().nullable(),
  meta_data: z.record(z.any())
});

export const DailyHoursSchema = z.object({
  id: z.string().uuid(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  location_id: z.string().uuid(),
  service_category_id: z.string().uuid().nullable(),
  date: z.string().datetime(),
  status: z.string(),
  open_time: z.string().nullable(),
  close_time: z.string().nullable(),
  closure_reason: z.string().nullable(),
  grade_note: z.string().nullable(),
  instructions_note: z.string().nullable(),
  global_note: z.string().nullable(),
  other_note: z.string().nullable(),
  fetch_run_id: z.string().uuid(),
  data_source: z.string(),
  is_baseline_derived: z.boolean(),
  content_hash: z.string(),
  meta_data: z.record(z.any())
});

export const FetchRunSchema = z.object({
  id: z.string().uuid(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  run_started_at: z.string().datetime(),
  run_completed_at: z.string().datetime().nullable(),
  location_id: z.string().uuid(),
  job_id: z.string().uuid().nullable(),
  prompt_version: z.string(),
  run_status: z.string(),
  error_message: z.string().nullable(),
  raw_html_content_id: z.string().uuid().nullable(),
  minimized_html_content_id: z.string().uuid().nullable(),
  cleaned_content_id: z.string().uuid().nullable(),
  mapped_content_id: z.string().uuid().nullable(),
  diff_content_id: z.string().uuid().nullable(),
  raw_hour_ids: z.array(z.string()),
  changed_hour_ids: z.array(z.string()),
  has_been_alerted: z.boolean().nullable(),
  meta_data: z.record(z.any())
});

// TypeScript Interfaces
export interface DestinationLocationImproved {
  id: string;
  created_at: Date;
  updated_at: Date;
  name: string;
  alias: string | null;
  link: string;
  is_enabled: boolean;
  is_visible: boolean;
  last_fetched: Date | null;
  last_parsed: Date | null;
  last_ingest: Date | null;
  meta_data: Record<string, any>;
  serviceCategories?: DestinationServiceCategoryImproved[];
  latest_run_id?: string;
}

export interface DestinationServiceCategoryImproved {
  id: string;
  created_at: Date;
  updated_at: Date;
  location_id: string;
  service_name: string;
  service_group: string | null;
  is_active: boolean;
  display_order: number | null;
  meta_data: Record<string, any>;
}

export interface DestinationDailyHoursImproved {
  id: string;
  created_at: Date;
  updated_at: Date;
  location_id: string;
  service_category_id: string | null;
  date: Date;
  status: string;
  open_time: string | null;
  close_time: string | null;
  closure_reason: string | null;
  grade_note: string | null;
  instructions_note: string | null;
  global_note: string | null;
  other_note: string | null;
  fetch_run_id: string;
  data_source: string;
  is_baseline_derived: boolean;
  content_hash: string;
  meta_data: Record<string, any>;
  location?: Partial<DestinationLocationImproved>;
  serviceCategory?: Partial<DestinationServiceCategoryImproved>;
}

export interface DestinationFetchRunImproved {
  id: string;
  created_at: Date;
  updated_at: Date;
  run_started_at: Date;
  run_completed_at: Date | null;
  location_id: string;
  job_id: string | null;
  prompt_version: string;
  run_status: 'running' | 'completed' | 'failed' | 'partial';
  error_message: string | null;
  raw_html_content_id: string | null;
  minimized_html_content_id: string | null;
  cleaned_content_id: string | null;
  mapped_content_id: string | null;
  diff_content_id: string | null;
  raw_hour_ids: string[];
  changed_hour_ids: string[];
  has_been_alerted: boolean | null;
  meta_data: Record<string, any>;
  location?: Partial<DestinationLocationImproved>;
  dailyHours?: DestinationDailyHoursImproved[];
}

// API Response Types
export interface GroupedHoursResponse {
  [locationId: string]: {
    location: Pick<DestinationLocationImproved, 'id' | 'name' | 'alias' | 'link'>;
    service_categories: {
      [serviceCategoryId: string]: {
        service_category: Pick<DestinationServiceCategoryImproved, 'id' | 'service_name' | 'service_group'> | null;
        hours: DestinationDailyHoursImproved[];
      }
    }
  }
}

// Request Types
export interface ListHoursRequest {
  location_ids: string; // comma-separated UUIDs
  start_date?: string; // YYYY-MM-DD
  end_date?: string; // YYYY-MM-DD
  limit?: number; // 1-1000, default 100
}

export interface ListRunsRequest {
  service_category_id?: string;
  limit?: number; // 1-500, default 50
  run_status?: string;
}

// Helper functions for query parameter assembly
function assembleListHoursQueryParams(filters?: Partial<ListHoursRequest>) {
  const queryParams = new URLSearchParams();
  if (!filters) {
    return queryParams;
  }

  if (filters.location_ids) {
    queryParams.append("location_ids", filters.location_ids);
  }

  if (filters.start_date) {
    queryParams.append("start_date", filters.start_date);
  }

  if (filters.end_date) {
    queryParams.append("end_date", filters.end_date);
  }

  if (filters.limit !== undefined) {
    queryParams.append("limit", filters.limit.toString());
  }

  return queryParams;
}

function assembleListRunsQueryParams(filters?: Partial<ListRunsRequest>) {
  const queryParams = new URLSearchParams();
  if (!filters) {
    return queryParams;
  }

  if (filters.service_category_id) {
    queryParams.append("service_category_id", filters.service_category_id);
  }

  if (filters.limit !== undefined) {
    queryParams.append("limit", filters.limit.toString());
  }

  if (filters.run_status) {
    queryParams.append("run_status", filters.run_status);
  }

  return queryParams;
}

// React Query Hooks

export function useListDestinationLocations() {
  return useQuery({
    queryKey: ["destinationHoursImproved", "locations"],
    queryFn: () => fetchApiGet<DestinationLocationImproved[]>("destination-hours-improved/locations"),
    retry: 1,
  });
}

export function useListDestinationHours(filters?: Partial<ListHoursRequest>) {
  const queryParams = assembleListHoursQueryParams(filters);

  return useQuery({
    queryKey: ["destinationHoursImproved", "hours", queryParams.toString()],
    queryFn: () => fetchApiGet<GroupedHoursResponse>("destination-hours-improved/hours", queryParams),
    retry: 1,
    enabled: !!filters?.location_ids?.length, // Only run if location_ids is provided
  });
}

export function useListDestinationRuns(locationId: string, filters?: Partial<ListRunsRequest>) {
  const queryParams = assembleListRunsQueryParams(filters);

  return useQuery({
    queryKey: ["destinationHoursImproved", "runs", locationId, queryParams.toString()],
    queryFn: () => fetchApiGet<DestinationFetchRunImproved[]>(`destination-hours-improved/locations/${locationId}/runs`, queryParams),
    retry: 1,
    enabled: !!locationId, // Only run if locationId is provided
  });
}
